import { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';
import { setAccessToken, setUserData, clearAuth } from '../authStore';
import { clearFaturamentoGraficoCache } from '../services/faturamento';

interface UserData {
  primeiro_nome: string;
  group: 'Diretores' | 'Administradores' | 'Cobradores';
}

interface AuthContextProps {
  user: UserData | null;
  token: string | null;
  login: (email: string, senha: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    (async () => {
      try {
        const res = await fetch(`${API_URL}/authentication/token/refresh/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (data.access) {
            setToken(data.access);
            setUser({
              primeiro_nome: data.primeiro_nome ?? '',
              group: data.group ?? '',
            });
            setAccessToken(data.access);
            setUserData({ primeiro_nome: data.primeiro_nome ?? '', group: data.group ?? '' });
          }
        }
      } catch {
        clearTimeout(timeoutId);
        if (!cancelled) clearAuth();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const login = async (email: string, senha: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/authentication/token/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password: senha }),
      });
      const data = await response.json();
      if (!response.ok || !data.access) return false;
      setToken(data.access);
      setUser({ primeiro_nome: data.primeiro_nome ?? '', group: data.group ?? '' });
      setAccessToken(data.access);
      setUserData({ primeiro_nome: data.primeiro_nome ?? '', group: data.group ?? '' });
      return true;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Erro no login:', err);
      return false;
    }
  };

  const logout = () => {
    const t = token;
    clearAuth();
    clearFaturamentoGraficoCache();
    setToken(null);
    setUser(null);
    fetch(`${API_URL}/authentication/logout/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    }).catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext deve ser usado dentro de AuthProvider');
  }
  return context;
};